import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import WhatsAppIcon from "../components/WhatsAppIcon";
import { WHATSAPP_URL } from "../lib/constants";
import { usePageMeta } from "../lib/usePageMeta";
import { api } from "../lib/api";
import { ShieldCheck, FileText, Star, Building2, Phone, AlertTriangle, BookOpen } from "lucide-react";

const CHECKLIST = [
  { icon: Building2, title: "CNPJ ativo e regularizado", text: "Verifique no site da Receita Federal se a empresa está ativa e há quanto tempo opera." },
  { icon: FileText, title: "Cadastur do Ministério do Turismo", text: "Toda agência de viagens legal precisa estar cadastrada. Consulte em cadastur.turismo.gov.br." },
  { icon: FileText, title: "Contrato formal por escrito", text: "Exija contrato com serviços contratados, valores, formas de pagamento e política de cancelamento." },
  { icon: Star, title: "Reputação e avaliações reais", text: "Procure avaliações no Google, Reclame Aqui e redes sociais — desconfie de perfis sem histórico." },
  { icon: Phone, title: "Atendimento humano identificado", text: "Uma boa agência tem pessoas com nome, WhatsApp e endereço físico. Fuja de números descartáveis." },
  { icon: AlertTriangle, title: "Nunca pague via Pix para pessoa física", text: "Pagamentos sempre para o CNPJ da agência. Boleto, cartão em nome jurídico ou transferência oficial." },
];

export default function Cuidados() {
  usePageMeta("Risco de Golpe: como evitar armadilhas na sua viagem — Viagens Orion", "Guia prático de cuidados antes de reservar uma viagem: como conferir agências no Cadastur, contratos, avaliações, sinais de golpe e checklist da Viagens Orion.");
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    api.get("/posts", { params: { category: "golpes", limit: 24 } })
      .then((r) => setPosts(r.data.items || []))
      .catch(() => {});
  }, []);
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1" data-testid="cuidados-page">
        <section className="bg-orion-purple text-white">
          <div className="container-orion py-20 md:py-28 max-w-4xl">
            <div className="cat-chip !text-orion-gold mb-4">Guia Orion · Leitura essencial</div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-semibold leading-tight">Cuidados ao planejar sua viagem.</h1>
            <p className="mt-6 text-lg text-white/80 max-w-2xl leading-relaxed">
              A cada semana, famílias inteiras perdem dinheiro com agências fantasmas, promessas milagrosas e pacotes que nunca aconteceram.
              Esta é a lista que a Orion usa — e que você também pode usar — para contratar sua viagem com segurança.
            </p>
          </div>
        </section>

        <section className="container-orion py-16 md:py-24 max-w-5xl">
          <div className="grid md:grid-cols-2 gap-x-10 gap-y-12">
            {CHECKLIST.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} data-testid={`cuidado-item-${i}`} className="border-t border-orion-gold pt-6">
                  <div className="flex items-start gap-4">
                    <Icon className="text-orion-orange shrink-0 mt-1" size={22} />
                    <div>
                      <h3 className="font-serif text-2xl text-orion-purple font-semibold mb-2">{item.title}</h3>
                      <p className="text-[#594A63] leading-relaxed">{item.text}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="bg-[#F8F4EB] py-16 md:py-24">
          <div className="container-orion max-w-3xl text-center">
            <ShieldCheck className="text-orion-orange mx-auto mb-4" size={32} />
            <h2 className="font-serif text-3xl md:text-4xl text-orion-purple font-semibold mb-4">A Viagens Orion cumpre todos esses critérios.</h2>
            <p className="text-[#594A63] leading-relaxed mb-8">
              Somos uma agência com CNPJ ativo, cadastrada no Cadastur, contrato formal para cada pacote, atendimento humano e transparente do primeiro contato ao retorno da viagem.
            </p>
            <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 text-xs uppercase tracking-widest hover:bg-[#1EBE57] transition-colors shadow-[0_10px_24px_rgba(37,211,102,0.35)]" data-testid="cuidados-cta">
              <WhatsAppIcon size={14} color="#ffffff" /> Conversar com a Orion
            </a>
          </div>
        </section>

        {/* Posts feed */}
        {posts.length > 0 && (
          <section className="container-orion py-16 md:py-24">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
              <div>
                <div className="text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-2">Aprofunde-se</div>
                <h2 className="font-serif text-3xl md:text-4xl text-orion-purple leading-tight">Nossos artigos sobre golpes e cuidados</h2>
              </div>
              <Link to="/categoria/golpes" className="text-xs uppercase tracking-widest text-orion-orange font-semibold hover:text-[#E04828]" data-testid="cuidados-see-all">
                Ver todos os artigos →
              </Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((p) => (
                <Link
                  key={p.slug}
                  to={`/artigo/${p.slug}`}
                  className="group border border-[#EFEAE0] p-6 bg-white hover:border-orion-orange hover:shadow-[0_18px_40px_rgba(44,17,57,0.10)] transition-all"
                  data-testid={`cuidados-post-${p.slug}`}
                >
                  <BookOpen size={20} className="text-orion-orange mb-3" strokeWidth={1.6} />
                  <h3 className="font-serif text-lg text-orion-purple leading-snug group-hover:text-orion-orange transition-colors line-clamp-3">
                    {p.title}
                  </h3>
                  {p.excerpt && (
                    <p className="mt-3 text-sm text-[#594A63] leading-relaxed line-clamp-3">{p.excerpt}</p>
                  )}
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
